import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {ProteinAlignmentKind, ProteinAlignment} from 'constants/AlignmentConstants';
import * as EventConstants from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {isFullProteinAlignment} from 'molbio/AlignmentUtil';
import {ProteinAlignmentState} from 'molbio/proteins/util/ProteinConstants';

/**
 * The state of the store, `alignmentStatesById`, contains a dictionary of alignment states keyed by
 * alignment ID. Each alignment state contains the following fields:
 *   - `alignment`: a summary alignment dict, or full alignment dict that is loaded when the alignment opened
 *   - `knownLinkedProteinIds`: a set of the IDs of the proteins that we know are linked to the alignment.
 *
 * If we haven't fetched the full alignment dictionary, `knownLinkedProteinIds` will only contain the
 * linked proteins that have already been opened. This is because when we open a protein, we add all of
 * the alignment summary dicts to the store, but we don't know all of the proteins that are linked to
 * an alignment from its summary dict.
 *
 * This store contains the source of truth of which alignments are linked or belong to a given protein,
 * not the protein dicts in the ProteinStore.
 */
class ProteinAlignmentStore extends Store {
  alignmentStatesById!: {[alignmentId: string]: ProteinAlignmentState};
  resetState(): void {
    this.alignmentStatesById = {};
  }

  getAlignmentStateById(alignmentId: string): ProteinAlignmentState | undefined {
    return this.alignmentStatesById[alignmentId];
  }

  _setAlignmentStateById(
    alignmentId: string,
    {alignment, knownLinkedProteinIds}: ProteinAlignmentState
  ): void {
    this.alignmentStatesById[alignmentId] = {alignment, knownLinkedProteinIds};
  }

  _deleteAlignmentStateById(alignmentId: string): void {
    delete this.alignmentStatesById[alignmentId];
  }

  // This list may not be complete if a protein has not been opened.
  getAlignments(proteinId: string): Array<ProteinAlignment> {
    // eslint-disable-next-line no-restricted-syntax
    const savedAlignmentStates = _.values(this.alignmentStatesById).filter(
      (alignmentState) => alignmentState.alignment.proteinId === proteinId
    );
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(savedAlignmentStates, 'alignment');
  }

  // This list may not be complete if a protein has not been opened.
  getLinkedAlignments(proteinId: string): Array<ProteinAlignment> {
    // eslint-disable-next-line no-restricted-syntax
    const linkedAlignmentStates = _.values(this.alignmentStatesById).filter((alignmentState) =>
      alignmentState.knownLinkedProteinIds.has(proteinId)
    );
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(linkedAlignmentStates, 'alignment');
  }

  _getUpdatedAlignmentState(alignmentSummaryDict: ProteinAlignment): ProteinAlignmentState {
    return (
      this.getAlignmentStateById(alignmentSummaryDict.id) ?? {
        alignment: alignmentSummaryDict,
        knownLinkedProteinIds: new Set(),
      }
    );
  }

  _getUpdatedLinkedAlignmentState(
    proteinId: string,
    alignmentSummaryDict: ProteinAlignment
  ): ProteinAlignmentState {
    if (!this.getAlignmentStateById(alignmentSummaryDict.id)) {
      return {
        alignment: alignmentSummaryDict,
        knownLinkedProteinIds: new Set([proteinId]),
      };
    } else {
      return reactUpdate(this.getAlignmentStateById(alignmentSummaryDict.id)!, {
        knownLinkedProteinIds: {$apply: (proteinIds) => proteinIds.add(proteinId)},
      });
    }
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.PROTEIN_OPEN_COMPLETED: {
        const {protein} = action;
        for (const alignment of protein.alignments) {
          this._setAlignmentStateById(alignment.id, this._getUpdatedAlignmentState(alignment));
        }
        for (const alignment of protein.linkedAlignments) {
          this._setAlignmentStateById(
            alignment.id,
            this._getUpdatedLinkedAlignmentState(protein.id, alignment)
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ALIGNMENT_OPEN_COMPLETED: {
        const {alignment} = action;
        // eslint-disable-next-line no-restricted-syntax
        const linkedProteinIds = _.pluck(alignment.rows.slice(1), 'originalProteinId').filter(
          (proteinId) => proteinId !== undefined
        );
        this._setAlignmentStateById(alignment.id, {
          alignment,
          knownLinkedProteinIds: new Set(linkedProteinIds),
        });
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ALIGNMENT_DELETE_COMPLETED: {
        const {alignmentId} = action;
        this._deleteAlignmentStateById(alignmentId);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ALIGNMENT_RENAME_COMPLETED: {
        const {alignmentId, name} = action;
        const alignmentState = this.getAlignmentStateById(alignmentId);
        if (alignmentState) {
          let newAlignmentState = reactUpdate(alignmentState, {
            alignment: {
              name: {$set: name},
            },
          });
          if (
            isFullProteinAlignment(alignmentState.alignment) &&
            alignmentState.alignment.alignmentType === ProteinAlignmentKind.CONSENSUS
          ) {
            newAlignmentState = reactUpdate(newAlignmentState, {
              alignment: {
                rows: {0: {name: {$set: name}}},
              },
            });
          }
          this._setAlignmentStateById(alignmentId, newAlignmentState);
          this.emitChange();
        }
        break;
      }
      default:
        break;
    }
    return true;
  }
}

const proteinAlignmentStore = new ProteinAlignmentStore(AppDispatcher);
export default proteinAlignmentStore;
